'use client'

import { useState, useRef } from 'react'

import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Mousewheel, Navigation, A11y } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import 'swiper/css/navigation'

import Button from '/react4xp/components/prototype/Button'
import Card from '/react4xp/components/prototype/Card'
import IconButton from '/react4xp/components/prototype/IconButton'
import ArrowRightSolid from '/react4xp/components/prototype/icons/ArrowRightSolid'

import type { FeaturedCardsProps } from './types'

export default function FeaturedCards({
	overline,
	title,
	cards = [],
	viewAllLink,
	prevLabel = 'Previous slide',
	nextLabel = 'Next slide',
}: FeaturedCardsProps) {
	const swiperRef = useRef<SwiperType | null>(null)
	const [isBeginning, setIsBeginning] = useState(true)
	const [isEnd, setIsEnd] = useState(false)

	const cardCount = cards.length
	const isCarousel = cardCount >= 3

	function handleSwiperUpdate(swiper: SwiperType) {
		setIsBeginning(swiper.isBeginning)
		setIsEnd(swiper.isEnd)
	}

	const viewAllButton = viewAllLink && (
		<Button
			variant="link"
			href={viewAllLink.url}
			isExternal={viewAllLink.isExternal}
		>
			{viewAllLink.text}
			{viewAllLink.isExternal ? (
				<ArrowUpRight className="h-[1lh] w-5 shrink-0" aria-hidden="true" />
			) : (
				<ArrowRight className="h-[1lh] w-5 shrink-0" aria-hidden="true" />
			)}
		</Button>
	)

	return (
		<section className="overflow-x-clip my-16">
			<div className="container">

				{/* Title block — overline shares a row with the view-all link, title spans its own full row */}
				<div className="flex flex-col">
					<div className="flex items-start justify-between gap-8">
						{overline && <p className="body text-black-950">{overline}</p>}

						{/* View-all: desktop (top-right, hidden on mobile) */}
						{viewAllLink && (
							<span className="hidden lg:flex shrink-0 ml-auto">
								{viewAllButton}
							</span>
						)}
					</div>

					{title && <h2 className="h2 text-black-950 max-w-175">{title}</h2>}
				</div>

				{/* Cards + arrows wrapper — mt-8 spaces the cards off the title wherever the
				    arrows row isn't rendered; at lg+ the carousel's arrows row supplies it instead.
				    Skipped entirely when there are no cards so an empty section doesn't leave its
				    padding/gap behind. */}
				{cardCount > 0 && (
					<div className={isCarousel ? 'flex flex-col gap-8 pb-6 mt-8 lg:mt-0' : 'flex flex-col gap-8 pb-6 mt-8'}>

					{/* Arrows row — only for carousel, hidden below lg */}
					{isCarousel && (
						<div className="hidden lg:flex justify-end">
							<span className="flex items-center gap-2">
								<IconButton
									icon={ArrowRightSolid}
									label={prevLabel}
									variant="round"
									disabled={isBeginning}
									onClick={() => swiperRef.current?.slidePrev()}
									className="rotate-180"
								/>
								<IconButton
									icon={ArrowRightSolid}
									label={nextLabel}
									variant="round"
									disabled={isEnd}
									onClick={() => swiperRef.current?.slideNext()}
								/>
							</span>
						</div>
					)}

					{/* Card layout — carousel at lg+ for 3+ cards */}
					{isCarousel ? (
						<>
							{/* Desktop carousel (lg+). Bleeds to the viewport edge below 2xl (1536px);
							    at 2xl+ it sits flush with .container again, same as the rest of the
							    section. slidesOffsetBefore/After (matching .container's lg:px-16,
							    cleared at 2xl) keep the first/last card aligned with the title above —
							    see ListPeople for why this uses Swiper's own offset params rather than
							    CSS padding on .swiper-wrapper. */}
							<div className="carousel-track hidden lg:block full-bleed min-w-0 2xl:w-full 2xl:mx-0">
								<Swiper
									modules={[Mousewheel, Navigation, A11y]}
									mousewheel={{ forceToAxis: true }}
									slidesPerView="auto"
									spaceBetween={32}
									slidesOffsetBefore={64}
									slidesOffsetAfter={64}
									breakpoints={{
										1536: { slidesPerView: 'auto', slidesOffsetBefore: 0, slidesOffsetAfter: 0 },
									}}
									onSwiper={(swiper) => {
										swiperRef.current = swiper
										handleSwiperUpdate(swiper)
									}}
									onSlideChange={(swiper) => handleSwiperUpdate(swiper)}
									onReachBeginning={(swiper) => handleSwiperUpdate(swiper)}
									onReachEnd={(swiper) => handleSwiperUpdate(swiper)}
									onFromEdge={(swiper) => handleSwiperUpdate(swiper)}
								>
									{cards.map((card, i) => (
										<SwiperSlide key={i} className="featured-card-slide">
											<Card
												image={card.image}
												label={card.label}
												title={card.title}
												url={card.url}
												isExternal={card.isExternal}
												titleSize="h3"
											/>
										</SwiperSlide>
									))}
								</Swiper>
							</div>

							{/* Mobile/tablet stack (below lg) — all cards, same md breakpoint as the static variants */}
							<div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-8">
								{cards.map((card, i) => (
									<Card
										key={i}
										image={card.image}
										label={card.label}
										title={card.title}
										url={card.url}
										isExternal={card.isExternal}
										titleSize="h3"
									/>
								))}
							</div>
						</>
					) : cardCount === 2 ? (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
							{cards.map((card, i) => (
								<Card
									key={i}
									image={card.image}
									label={card.label}
									title={card.title}
									url={card.url}
									isExternal={card.isExternal}
									titleSize="h3"
								/>
							))}
						</div>
					) : cardCount === 1 ? (
						<div className="grid grid-cols-1 md:grid-cols-2">
							<Card
								image={cards[0].image}
								label={cards[0].label}
								title={cards[0].title}
								url={cards[0].url}
								isExternal={cards[0].isExternal}
								titleSize="h3"
							/>
						</div>
					) : null}

				</div>
				)}

				{/* View-all: mobile (below cards, hidden at lg+) */}
				{viewAllLink && (
					<span className="flex lg:hidden">
						{viewAllButton}
					</span>
				)}

			</div>
		</section>
	)
}
